import {
    green,
    cyan,
    blue,
    magenta,
    lime,
    purple,
    geekblue,
    orange,
    grey,
    gold,
    red,
    yellow,
} from '@ant-design/colors';

// not gonna lie: https://stackoverflow.com/questions/39494689/is-it-possible-to-restrict-number-to-a-certain-range/70307091#70307091
type Enumerate<N extends number, Acc extends number[] = []> =
    Acc['length'] extends N ? Acc[number] : Enumerate<N, [...Acc, Acc['length']]>;

type IntRange<F extends number, T extends number> = Exclude<Enumerate<T>, Enumerate<F>>;
type StandardColorRanges = IntRange<1, 11>;

// but this is original content ;)...
type AntColor<T extends string> = Record<`${T}${StandardColorRanges}`, string>;

interface RunwayColors {
    primary: string;
    primaryLight: string;
    statusGreen: string;
    statusYellow: string;
    statusRed: string;
    statusGrey: string;
    info: string;
    // below colors are deprecated! use antd greys instead
    darkGrey: string;
    darkTextGrey: string;
    lightBackgroundGrey: string;
    androidGreen: string;
    white: string;
    black: string;
    // Navigation-specific colors
    navGrey: string;
}

export const runwayColors: RunwayColors = {
    primary: '#6A7FDB',
    primaryLight: '#A7B4EE',
    statusGreen: '#6EC78B',
    statusYellow: '#FFAD05',
    statusRed: '#EF2D56',
    statusGrey: 'var(--rw-color-status-grey)',
    info: '#6A7FDB',
    // theme-aware neutrals (single source of truth is CSS variables in index.less)
    lightBackgroundGrey: 'var(--rw-color-bg-layout)',
    darkGrey: 'var(--rw-color-border-strong)',
    darkTextGrey: 'var(--rw-color-text-secondary)',
    androidGreen: '#BAE637',
    white: 'var(--rw-color-bg-container)',
    black: 'var(--rw-color-text)',
    // Navigation-specific colors
    navGrey: '#737373',
};

export const summaryViewColors = {
    success: '#6EC78B',
    warning: '#FFAD05',
    error: '#EF2D56',
    successLight: '#6EC78B26',
    warningLight: '#FFAD0526',
    errorLight: '#EF2D5626',
};

/**
 * Creates an rgba() color string from a theme-aware color.
 * Works with colors from antGrey, rwColors, etc.
 *
 * @example
 * colorWithAlpha(antGrey.grey5, 0.5)    // "rgba(var(--rw-ant-grey-5-rgb), 0.5)"
 * colorWithAlpha(rwColors.primary, 0.2) // "rgba(var(--rw-color-primary-rgb), 0.2)"
 */
export const colorWithAlpha = (color: string, alpha: number) => {
    // Extract the var(...) from rgb(var(...)) format
    const match = color.match(/rgb\((var\([^)]+\))\)/);
    if (match) {
        return `rgba(${match[1]}, ${alpha})`;
    }
    // If already a raw var(...) reference, use directly
    if (color.startsWith('var(')) {
        return `rgba(${color}, ${alpha})`;
    }
    // Fallback for hex colors (legacy support)
    return hexStringToRGBAString(color, alpha);
};

export const antGrey: AntColor<'grey'> = {
    // Theme-aware neutrals. Use directly for solid colors, or with colorWithAlpha() for transparency.
    grey1: 'rgb(var(--rw-ant-grey-1-rgb))',
    grey2: 'rgb(var(--rw-ant-grey-2-rgb))',
    grey3: 'rgb(var(--rw-ant-grey-3-rgb))',
    grey4: 'rgb(var(--rw-ant-grey-4-rgb))',
    grey5: 'rgb(var(--rw-ant-grey-5-rgb))',
    grey6: 'rgb(var(--rw-ant-grey-6-rgb))',
    grey7: 'rgb(var(--rw-ant-grey-7-rgb))',
    grey8: 'rgb(var(--rw-ant-grey-8-rgb))',
    grey9: 'rgb(var(--rw-ant-grey-9-rgb))',
    grey10: 'rgb(var(--rw-ant-grey-10-rgb))',
};

// Runway theme colors - use directly or with colorWithAlpha() for transparency
export const rwColors = {
    primary: 'rgb(var(--rw-color-primary-rgb))',
    statusGreen: 'rgb(var(--rw-color-status-green-rgb))',
    statusYellow: 'rgb(var(--rw-color-status-yellow-rgb))',
    statusRed: 'rgb(var(--rw-color-status-red-rgb))',
};

export const antGold = gold.reduce((acc, color, i) => {
    return {
        ...acc,
        [`gold${i + 1}`]: color,
    };
}, {} as AntColor<'gold'>);

export const antYellow = yellow.reduce((acc, color, i) => {
    return {
        ...acc,
        [`yellow${i + 1}`]: color,
    };
}, {} as AntColor<'yellow'>);

export const antOrange = orange.reduce((acc, color, i) => {
    return {
        ...acc,
        [`orange${i + 1}`]: color,
    };
}, {} as AntColor<'orange'>);

export const antRed = red.reduce((acc, color, i) => {
    return {
        ...acc,
        [`red${i + 1}`]: color,
    };
}, {} as AntColor<'red'>);

export const antBlue = blue.reduce((acc, color, i) => {
    return {
        ...acc,
        [`blue${i + 1}`]: color,
    };
}, {} as AntColor<'blue'>);

export const antCyan = cyan.reduce((acc, color, i) => {
    return {
        ...acc,
        [`cyan${i + 1}`]: color,
    };
}, {} as AntColor<'cyan'>);

export const antMagenta = magenta.reduce((acc, color, i) => {
    return {
        ...acc,
        [`magenta${i + 1}`]: color,
    };
}, {} as AntColor<'magenta'>);

export const antGreen = green.reduce((acc, color, i) => {
    return {
        ...acc,
        [`green${i + 1}`]: color,
    };
}, {} as AntColor<'green'>);

export const antLime = lime.reduce((acc, color, i) => {
    return {
        ...acc,
        [`lime${i + 1}`]: color,
    };
}, {} as AntColor<'lime'>);

export const statusElement: React.CSSProperties = {
    margin: 12,
    height: 16,
    width: 16,
    borderRadius: 8,
    borderColor: runwayColors.darkGrey,
    borderWidth: 2,
};

export const pageHeaderStyle: React.CSSProperties = {
    paddingLeft: 0,
    paddingTop: 0,
    paddingBottom: '12px',
};

export const titleStyle: React.CSSProperties = {
    fontWeight: 600,
    color: antGrey.grey7,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontSize: 16,
    lineHeight: '28px',
};

// Navigation shared styles
export const getNavDropdownMenuStyle = (sc: { dropdownBg: string; border: string }): React.CSSProperties => ({
    backgroundColor: sc.dropdownBg,
    borderRadius: 6,
    border: `1px solid ${sc.border}`,
    padding: 0,
});

export const getNavSectionTitleStyle = (sc: { textSecondary: string }): React.CSSProperties => ({
    textTransform: 'uppercase',
    fontSize: 12,
    fontWeight: 400,
    color: sc.textSecondary,
    letterSpacing: '0.1em',
    padding: '8px 16px',
});

export const navMenuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    cursor: 'pointer',
};

export const getNavMenuItemHoverStyle = (sc: { dropdownHoverBg: string }): React.CSSProperties => ({
    backgroundColor: sc.dropdownHoverBg,
});

export const getNavBorderContainerStyle = (sc: { border: string }): React.CSSProperties => ({
    border: `1px solid ${sc.border}`,
    borderRadius: 8,
    overflow: 'hidden',
});

export const navIconButtonStyle: (sc: { icon: string }) => React.CSSProperties = (sc) => ({
    padding: 4,
    height: 'auto',
    color: sc.icon,
});

export const navFlexItemStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
};

export const releaseMetaTextStyle: React.CSSProperties = {
    textTransform: 'uppercase',
    fontSize: 11,
    letterSpacing: '0.1em',
    textAlign: 'right',
};

export const appHomeCardsWidgetTitleStyle: React.CSSProperties = {
    fontWeight: 400,
    color: antGrey.grey7,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontSize: 10,
    lineHeight: '20px',
};

export const blurryBackdropStyle: React.CSSProperties = {
    background: 'var(--rw-backdrop-blur-bg)',
    backdropFilter: 'blur(20px)',
};

export const tagStyle: React.CSSProperties = {
    fontWeight: 700,
    textTransform: 'uppercase',
    fontSize: 10,
    letterSpacing: '0.1em',
};

export const releaseStepDetailsContainerModuleStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 20,
    paddingBottom: 20,
    borderColor: antGrey.grey4,
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: 8,
    flexGrow: 1,
};

export const releaseSettingsDescriptionsStyle: React.CSSProperties = {
    fontWeight: 600,
    width: '30%',
    minWidth: 300,
    color: antGrey.grey7,
};

export const releaseStepDetailsContainerModuleBackgroundStyle: React.CSSProperties = {
    backgroundColor: antGrey.grey2,
};

export const releaseStepModuleTitleStyle: React.CSSProperties = {
    fontWeight: 600,
    color: antGrey.grey8,
    fontSize: 14,
};

export const releaseStepReviewItemStateStyle: React.CSSProperties = {
    fontWeight: 600,
    color: antGrey.grey8,
    letterSpacing: '0.1em',
    fontSize: 10,
};

export const releaseStepModuleHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
};

export const pageContentStyle: React.CSSProperties = {
    paddingTop: 24,
    paddingBottom: 24,
};

export const buildContainerStyle: React.CSSProperties = {
    backgroundColor: antGrey.grey3,
    borderRadius: 8,
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 6,
    paddingBottom: 6,
};

export const rightPanelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minWidth: 240,
};

export const middlePanelStyle: React.CSSProperties = {
    flexGrow: 1,
    marginRight: 24,
    paddingRight: 12,
    marginLeft: 24,
    display: 'flex',
    justifyContent: 'flex-end',
    minWidth: 300,
};

export const leftPanelStyle: React.CSSProperties = {
    flexBasis: 0,
};

// live release and release overview
export const leftLiveModulePanelStyle: React.CSSProperties = {
    width: 120,
    minWidth: 120,
    display: 'flex',
    flexDirection: 'column',
};

export const liveReleaseModuleTitleStyle: React.CSSProperties = {
    fontSize: 14,
    color: antGrey.grey8,
};

export const contentStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', marginTop: 8 };

export const releasePilotModuleStyle: React.CSSProperties = {
    display: 'flex',
    backgroundColor: runwayColors.lightBackgroundGrey,
    borderRadius: 8,
    padding: 24,
};

export const appHomeModuleStyle: React.CSSProperties = {
    flexGrow: 1,
    borderColor: antGrey.grey4,
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 16,
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 12,
    paddingBottom: 24,
    marginBottom: 24,
};

export const drawerCardHeadStyles: React.CSSProperties = {
    backgroundColor: antGrey.grey3,
    borderRadius: '0px',
    borderTop: `1px solid ${antGrey.grey4}`,
    borderBottom: `1px solid ${antGrey.grey4}`,
    padding: `1px 16px 1px 20px`,
};

export const drawerCardBodyStyles: React.CSSProperties = {
    padding: '16px 16px 44px 16px',
};

export const BUILD_BUCKET_COLORS = [geekblue[4], cyan[4], red[4], purple[4], magenta[4], yellow[4]];

export const AVATAR_COLOR_PALETTE = [cyan[5], magenta[5], purple[5], geekblue[5], orange[5], lime[5]];

export const getAvatarColorForName: (nameString: string) => string = (nameString) => {
    const allColors = AVATAR_COLOR_PALETTE;

    if (nameString === '') {
        return grey.primary;
    }

    const firstChar = nameString.charAt(0);

    if (firstChar.match(/[a-c]/i)) {
        return allColors[0];
    } else if (firstChar.match(/[d-f]/i)) {
        return allColors[1];
    } else if (firstChar.match(/[g-i]/i)) {
        return allColors[2];
    } else if (firstChar.match(/[j-l]/i)) {
        return allColors[3];
    } else if (firstChar.match(/[m-o]/i)) {
        return allColors[4];
    } else if (firstChar.match(/[p-r]/i)) {
        return allColors[5];
    } else if (firstChar.match(/[s-u]/i)) {
        return allColors[6];
    } else if (firstChar.match(/[v-x]/i)) {
        return allColors[7];
    } else if (firstChar.match(/[y-z]/i)) {
        return allColors[8];
    } else {
        return grey.primary;
    }
};

/**
 * @deprecated Use `colorWithAlpha()` instead for dark mode support.
 * This function only works with hex colors and will break with CSS variables.
 *
 * @example
 * // Instead of:
 * hexStringToRGBAString(runwayColors.primary, 0.5)
 *
 * // Use:
 * colorWithAlpha(rwColors.primary, 0.5)
 */
export const hexStringToRGBAString = (hex: string, alpha = 1) => {
    let c;

    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        c = hex.substring(1).split('');
        if (c.length == 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c = '0x' + c.join('');
        return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + `,${alpha})`;
    }

    return 'rgba(0, 0, 0, 1)';
};

export const betaSoakContainerModuleStyle: React.CSSProperties = {
    display: 'flex',
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 18,
    paddingBottom: 18,
    borderColor: antGrey.grey4,
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: 12,
    flexGrow: 1,
    width: '100%',
    justifyContent: 'stretch',
    alignItems: 'flex-start',
    flexShrink: 0,
    backgroundColor: antGrey.grey2,
};

export const frRowIgnoredStyle: React.CSSProperties = {
    opacity: 0.3,
};

export const formItemHelpStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 12,
    marginBottom: 6,
};

// Release Calendar external events
export const EXTERNAL_EVENT_BACKGROUND_COLOR = 'var(--rw-external-event-bg)';

type RunwayThemeMode = 'light' | 'dark';

const RUNWAY_THEME_CSS_VARS: Record<RunwayThemeMode, Record<string, string>> = {
    light: {
        '--rw-color-primary-rgb': '106, 127, 219',
        '--rw-color-status-green-rgb': '69, 203, 133',
        '--rw-color-status-yellow-rgb': '255, 173, 5',
        '--rw-color-status-red-rgb': '239, 45, 86',

        '--rw-color-primary': '#6A7FDB',
        '--rw-color-bg-layout': '#f0f2f5',
        '--rw-color-bg-container': '#ffffff',
        '--rw-color-text': '#262626',
        '--rw-color-text-secondary': '#8c8c8c',
        '--rw-color-border': '#d9d9d9',
        '--rw-color-border-strong': '#434343',
        '--rw-color-status-grey': '#8c8c8c',
        '--rw-calendar-today-bg': '#f0f2fb',
        '--rw-shadow-card': '4px 4px 0px 0px rgba(0, 0, 0, 0.1)',
        '--rw-color-connector': 'rgba(89, 89, 89, 0.5)',
        '--rw-filter-drop-shadow': 'drop-shadow(rgba(0, 0, 0, 0.2) 0px 4px 5px)',
        '--rw-shadow-sm': '0px 1.5px 3px rgba(0, 0, 0, 0.12)',
        '--rw-shadow-md': '0px 4px 8px rgba(0, 0, 0, 0.2)',
        '--rw-shadow-lg': '0px 9px 28px 8px rgba(0, 0, 0, 0.05), 0px 3px 6px -4px rgba(0, 0, 0, 0.12)',
        '--rw-overlay-bg': 'rgba(0, 0, 0, 0.7)',
        '--rw-border-muted': 'rgba(140, 140, 140, 0.75)',

        '--rw-ant-grey-1-rgb': '255, 255, 255',
        '--rw-ant-grey-2-rgb': '250, 250, 250',
        '--rw-ant-grey-3-rgb': '245, 245, 245',
        '--rw-ant-grey-4-rgb': '240, 240, 240',
        '--rw-ant-grey-5-rgb': '217, 217, 217',
        '--rw-ant-grey-6-rgb': '191, 191, 191',
        '--rw-ant-grey-7-rgb': '140, 140, 140',
        '--rw-ant-grey-8-rgb': '89, 89, 89',
        '--rw-ant-grey-9-rgb': '67, 67, 67',
        '--rw-ant-grey-10-rgb': '38, 38, 38',

        '--rw-backdrop-blur-bg': 'rgba(255, 255, 255, 0.5)',
        '--rw-external-event-bg': '#595959',
        '--rw-demo-mode-bg': 'rgba(255, 255, 255, 0.3)',
        '--rw-chart-container-bg': 'rgba(0, 0, 0, 0.02)',
        '--rw-overlay-dark': 'rgba(0, 0, 0, 0.3)',
    },
    dark: {
        '--rw-color-primary-rgb': '106, 127, 219',
        '--rw-color-status-green-rgb': '69, 203, 133',
        '--rw-color-status-yellow-rgb': '255, 173, 5',
        '--rw-color-status-red-rgb': '239, 45, 86',

        '--rw-color-primary': '#6A7FDB',
        '--rw-color-bg-layout': '#0f0f0f',
        '--rw-color-bg-container': '#141414',
        '--rw-color-text': '#f5f5f5',
        '--rw-color-text-secondary': '#bfbfbf',
        '--rw-color-border': '#303030',
        '--rw-color-border-strong': '#595959',
        '--rw-color-status-grey': '#8c8c8c',
        '--rw-calendar-today-bg': '#1a2233',
        '--rw-shadow-card': '4px 4px 0px 0px rgba(0, 0, 0, 0.6)',
        '--rw-color-connector': 'rgba(191, 191, 191, 0.35)',
        '--rw-filter-drop-shadow': 'drop-shadow(rgba(0, 0, 0, 0.7) 0px 4px 5px)',
        '--rw-shadow-sm': '0px 1.5px 3px rgba(0, 0, 0, 0.5)',
        '--rw-shadow-md': '0px 4px 8px rgba(0, 0, 0, 0.7)',
        '--rw-shadow-lg': '0px 9px 28px 8px rgba(0, 0, 0, 0.5), 0px 3px 6px -4px rgba(0, 0, 0, 0.7)',
        '--rw-overlay-bg': 'rgba(0, 0, 0, 0.8)',
        '--rw-border-muted': 'rgba(191, 191, 191, 0.35)',

        '--rw-ant-grey-1-rgb': '255, 255, 255',
        '--rw-ant-grey-2-rgb': '20, 20, 20',
        '--rw-ant-grey-3-rgb': '31, 31, 31',
        '--rw-ant-grey-4-rgb': '38, 38, 38',
        '--rw-ant-grey-5-rgb': '48, 48, 48',
        '--rw-ant-grey-6-rgb': '89, 89, 89',
        '--rw-ant-grey-7-rgb': '191, 191, 191',
        '--rw-ant-grey-8-rgb': '217, 217, 217',
        '--rw-ant-grey-9-rgb': '240, 240, 240',
        '--rw-ant-grey-10-rgb': '15, 15, 15',

        '--rw-backdrop-blur-bg': 'rgba(0, 0, 0, 0.35)',
        '--rw-external-event-bg': '#303030',
        '--rw-chart-container-bg': 'rgba(255, 255, 255, 0.02)',
        '--rw-overlay-dark': 'rgba(0, 0, 0, 0.5)',
        '--rw-demo-mode-bg': 'rgba(0, 0, 0, 0.2)',
    },
};

export function applyRunwayTheme(mode: RunwayThemeMode) {
    if (typeof document === 'undefined') {
        return;
    }

    const vars = RUNWAY_THEME_CSS_VARS[mode];
    for (const [key, value] of Object.entries(vars)) {
        document.documentElement.style.setProperty(key, value);
    }
}
